import type { SupportCitation } from 'hooks/ai/mutations/useAskSupportAssistantMutation';

export const SUPPORT_CHAT_TTL = 12 * 60 * 60 * 1000;
const PREFIX = 'restore.support-chat.';
// Older saved chats may omit nullable metadata selected by the network operation.
export type StoredSupportCitation = Omit<SupportCitation, 'title' | 'heading'> &
  Partial<Pick<SupportCitation, 'title' | 'heading'>>;
export type ChatMessage = {
  role: 'user' | 'assistant';
  text: string;
  citations?: StoredSupportCitation[];
};
export type SupportChat = {
  version: 1;
  conversationId?: string;
  messages: ChatMessage[];
  draft: string;
  expiresAt: number;
};
export const emptySupportChat = (): SupportChat => ({
  version: 1,
  messages: [],
  draft: '',
  expiresAt: 0,
});
export const supportChatKey = (userId: number, tenantId: number) =>
  `${PREFIX}${userId}.${tenantId}`;
export function removeSupportChat(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* Storage may be disabled. */
  }
}
export function clearSupportChats() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach(removeSupportChat);
  } catch {
    /* In-memory sessions are cleared by logout. */
  }
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isValidCitation(value: unknown): value is StoredSupportCitation {
  return (
    isRecord(value) &&
    typeof value.url === 'string' &&
    typeof value.urlPath === 'string' &&
    (value.title == null || typeof value.title === 'string') &&
    (value.heading == null || typeof value.heading === 'string')
  );
}

function isValidChatMessage(value: unknown): value is ChatMessage {
  return (
    isRecord(value) &&
    (value.role === 'user' || value.role === 'assistant') &&
    typeof value.text === 'string' &&
    (value.citations === undefined ||
      (Array.isArray(value.citations) && value.citations.every(isValidCitation)))
  );
}

function isValidStoredChat(value: unknown, now: number): value is SupportChat {
  return (
    isRecord(value) &&
    value.version === 1 &&
    typeof value.expiresAt === 'number' &&
    Number.isFinite(value.expiresAt) &&
    value.expiresAt > now &&
    typeof value.draft === 'string' &&
    value.draft.length <= 2000 &&
    (value.conversationId === undefined || typeof value.conversationId === 'string') &&
    Array.isArray(value.messages) &&
    value.messages.every(isValidChatMessage)
  );
}

export function readSupportChat(key: string, now = Date.now()): SupportChat {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptySupportChat();
    const value = JSON.parse(raw);
    if (!isValidStoredChat(value, now)) {
      removeSupportChat(key);
      return emptySupportChat();
    }
    return value;
  } catch {
    removeSupportChat(key);
    return emptySupportChat();
  }
}
export function saveSupportChat(key: string, chat: SupportChat) {
  try {
    if (!chat.expiresAt) removeSupportChat(key);
    else localStorage.setItem(key, JSON.stringify(chat));
  } catch {
    /* The current chat remains usable in memory. */
  }
}
